// Fatigue Management dashboard.
//
// Rebuilt on shadcn/ui + scoped Tailwind, matching the ROI Reporting rebuild:
// the page owns a .fatigue-page scope so Tailwind utilities apply here without
// Preflight touching the MUI pages around it, and `dark` is driven from the MUI
// palette mode so the two systems agree about theme.

import React from 'react';
import { useTheme } from '@mui/material/styles';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { FatigueFilters } from '../data/types';
import { DEFAULT_FILTERS, useFatigueSummary } from '../data/useFatigueData';
import { NOT_MEDICAL } from '../data/methodology';
import { GlobalFilters } from '../components/GlobalFilters';
import { NoteLine } from '../components/primitives';
import { OverviewSection } from '../sections/OverviewSection';
import { ActionsSection, CohortRiskSection, MethodologySection, OperationsSection } from '../sections/sections';
import '../../../../../styles/fatigue-shadcn.css';

type SectionKey = 'overview' | 'operations' | 'cohorts' | 'actions' | 'methodology';

const SECTIONS: { key: SectionKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'operations', label: 'Operations safety' },
  { key: 'cohorts', label: 'Cohort risk' },
  { key: 'actions', label: 'Wellbeing actions' },
  { key: 'methodology', label: 'Methodology' },
];

export const FatigueManagement: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [section, setSection] = React.useState<SectionKey>('overview');
  const [filters, setFilters] = React.useState<FatigueFilters>(DEFAULT_FILTERS);

  // The same query every section reads — cached, so this costs no extra request.
  // It is here for the filter bar's own options and caveats, which have to be
  // driven by the real cohorts rather than a hardcoded list.
  const { data } = useFatigueSummary(filters);

  return (
    <div className={cn('fatigue-page flex flex-col gap-6 p-1', isDark && 'dark')}>
      {/* Title and section nav share a row, as on the ROI page, so the nav
          doesn't eat vertical space on smaller screens (FM-13). */}
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold'>Fatigue Management</h1>
          <p className='mt-1 text-sm text-muted-foreground'>
            Cohort-level fatigue exposure, load alignment and scheduling risk across the workforce.
          </p>
        </div>
        <nav aria-label='Dashboard sections' className='flex flex-wrap gap-2'>
          {SECTIONS.map((item) => (
            <Button
              key={item.key}
              variant={section === item.key ? 'default' : 'outline'}
              size='sm'
              aria-current={section === item.key ? 'page' : undefined}
              onClick={() => setSection(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </nav>
      </div>

      {/* FM-15 — one filter bar for every section. */}
      <GlobalFilters
        filters={filters}
        sites={data?.sites ?? []}
        onChange={setFilters}
        suppressedCohorts={data?.caveats.suppressedCohorts ?? []}
        minCohortSize={data?.caveats.minCohortSize}
        window={data?.window}
      />

      {section === 'overview' && <OverviewSection filters={filters} />}
      {section === 'operations' && <OperationsSection filters={filters} />}
      {section === 'cohorts' && <CohortRiskSection filters={filters} />}
      {section === 'actions' && <ActionsSection />}
      {section === 'methodology' && <MethodologySection />}

      <NoteLine>{NOT_MEDICAL}</NoteLine>
    </div>
  );
};
